import React, { useState } from 'react';
import { Paper, TextField, Grid, Box, Button, Alert } from '@mui/material';
import { useTranslation } from 'react-i18next';
import SitesService from '../../../services/SitesService';
import SnackbarUtils from '../../../utils/SnackbarUtils';
import { useSitesConfig } from '../SitesContext';
import { prepareFieldUpdates } from '../utils/fieldUtils';

const TextsTab = () => {
  const { t } = useTranslation();
  const { config, handleChange, isFieldConfigurable, isFieldFixed, loadConfig } = useSitesConfig();
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    try {
      setSaving(true);
      
      const updates = {};
      Object.keys(config.siteConfig.texts || {}).forEach(textKey => {
        const fieldPath = `siteConfig.texts.${textKey}`;
        if (isFieldConfigurable(fieldPath)) {
          updates[fieldPath] = config.siteConfig.texts[textKey];
        }
      });

      if (Object.keys(updates).length === 0) {
        SnackbarUtils.warning(t('no_editable_fields') || 'No hay campos editables en esta sección');
        return;
      }

      const preparedUpdates = prepareFieldUpdates(updates);
      await SitesService.updateFields(preparedUpdates);
      await loadConfig();
      
      SnackbarUtils.success(t('section_saved_successfully') || 'Sección guardada exitosamente');
    } catch (err) {
      console.error('Error saving texts:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message;
      SnackbarUtils.error(errorMessage || (t('error_saving_section') || 'Error al guardar la sección'));
    } finally {
      setSaving(false);
    }
  };

  // Solo mostrar campos de texts que son configurables
  const textFields = Object.keys(config.siteConfig.texts || {})
    .map(textKey => ({
      key: textKey,
      path: `siteConfig.texts.${textKey}`
    }))
    .filter(field => isFieldConfigurable(field.path));

  const hasEditableFields = textFields.length > 0;

  if (!hasEditableFields) {
    return (
      <Paper sx={{ padding: 3, mt: 2 }}>
        <Alert severity="info">
          {t('no_editable_fields_in_section') || 'No hay campos editables en esta sección para el template actual'}
        </Alert>
      </Paper>
    );
  }

  return (
    <Paper sx={{ padding: 3, mt: 2 }}>
      <Grid container spacing={2}>
        {textFields.map((field) => {
          const value = config.siteConfig.texts[field.key] || '';

          return (
            <Grid item xs={12} md={6} key={field.key}>
              <TextField
                fullWidth
                label={t(field.key) || field.key}
                value={value}
                onChange={(e) => handleChange(field.path, e.target.value)}
              />
            </Grid>
          );
        })}
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={saving}>
              {saving ? (t('saving') || 'Guardando...') : (t('save') || 'Guardar')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default TextsTab;
